import { formatDistanceToNow, parseISO, format } from "date-fns";
import axios from 'axios';
import {SessionManager, EnumUtility, SharedService, ClientType} from '../components/ComponentsLibrary'; // 🔍 ADD: Import SessionManager
import ApiManager from './ApiManager';        // 🔍 ADD: Import ApiManager (needed for createAndSubmitDocTransferLog)
import { MasterEnum } from "../enums/MasterEnum";
import ScrapeDataManager from './ScrapeDataManager';
import LoggerUtility from './LoggerUtility';
import SiteTimeUtility from './SiteTimeUtility';

let _cachedConfig = null;
const _getAppConfig = async () => {
  if (_cachedConfig) return _cachedConfig;
  try {
    const response = await fetch('/config.json');
    _cachedConfig = await response.json();
  } catch {
    _cachedConfig = {};
  }
  return _cachedConfig;
};

// Utility to generate the format map for a given Date object
const dateFormatMap = (date) => ({
  MM: String(date.getMonth() + 1).padStart(2, '0'), // Month (2-digit)
  M: String(date.getMonth() + 1),                  // Month (1-digit)
  DD: String(date.getDate()).padStart(2, '0'),     // Day (2-digit)
  D: String(date.getDate()),                       // Day (1-digit)
  YYYY: date.getFullYear(),                        // Year (4-digit)
  YY: String(date.getFullYear()).slice(-2),        // Year (2-digit)
  hh: String(date.getHours() % 12 || 12).padStart(2, '0'), // Hours (12-hour, 2-digit)
  h: String(date.getHours() % 12 || 12),           // Hours (12-hour, 1-digit)
  HH: String(date.getHours()).padStart(2, '0'),    // Hours (24-hour, 2-digit)
  H: String(date.getHours()),                      // Hours (24-hour, 1-digit)
  mm: String(date.getMinutes()).padStart(2, '0'),  // Minutes (2-digit)
  m: String(date.getMinutes()),                    // Minutes (1-digit)
  ss: String(date.getSeconds()).padStart(2, '0'),  // Seconds (2-digit)
  s: String(date.getSeconds()),                    // Seconds (1-digit)
  A: date.getHours() >= 12 ? 'PM' : 'AM',          // AM/PM
  a: date.getHours() >= 12 ? 'pm' : 'am',          // am/pm
});

// Mock data constants - centralized definition
const MOCK_DATA_TARGET = {
  TARGET_TEST_DATE: '20260811',  // YYYYMMDD format
  TARGET_USER_ID: 15              // User ID for mock testing
};

const parseDateValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
    const [year, month, day] = trimmedValue.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const isoMatch = trimmedValue.match(/^\s*(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}:?\d{2})?\s*$/);
  if (isoMatch) {
    const [, year, month, day, hour, minute, second = '00', milliseconds = '0'] = isoMatch;
    const parsed = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      Number((String(milliseconds).padEnd(3, '0')).slice(0, 3))
    );
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(trimmedValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value, format = "MM/DD/YYYY hh:mm A") => {
  const date = parseDateValue(value);
  if (!date) return '';

  const formatMap = dateFormatMap(date);

  // Replace tokens in the format string with actual values
  return format.replace(/MM|M|DD|D|YYYY|YY|hh|h|HH|H|mm|m|ss|s|A|a/g, (match) => formatMap[match] || match);
};


 const cleanString = (val) => {
  const placeholders = ['N/A', 'PLACEHOLDER', 'Unknown'];
  if (typeof val !== 'string') return val;
  return placeholders.includes(val.trim()) ? '' : val;
}


/**
* Replaces placeholders ({0}, {1}, ...) in a string with provided values.
* @param {string} template - The template string with placeholders.
* @param {Array<string|number>} values - An array of values to replace the placeholders.
* @returns {string} - The formatted string.
*/
const formatString = (template, values) => {
  return template.replace(/{(\d+)}/g, (match, index) => {
    return typeof values[index] !== "undefined" ? values[index] : match;
  });
};

class StringBuilder {
  constructor() {
    this.content = [];
  }

  // Append text
  append(text) {
    this.content.push(text);
  }

  // Append text with a new line
  appendLine(text) {
    this.content.push(text + "\n");
  }

  // Get final string
  getString() {
    return this.content.join("");
  }

  // Clear content
  clear() {
    this.content = [];
  }
}

// Define the comparator method
const dateSortComparator = (v1, v2, param1, param2) => {
  const rawDate1 = param1.value;
  const rawDate2 = param2.value;
  const date1 = new Date(rawDate1);
  const date2 = new Date(rawDate2);
  return date1 - date2;
};

const displayDateFormatted = (param) => {
  const rawDate = param.value; // Raw value from `field`
  if (!rawDate) return "No date available"; // Handle missing dates
  return formatDistanceToNow(parseISO(rawDate), { addSuffix: true });
};

// Get the current date in ISO format
const getCurrentDate = () => {
  const date = new Date();
  return date.toISOString();
};

// Calculate the difference in days between two dates
const calculateDateDifference = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffTime = Math.abs(endDate - startDate);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convert to days
};

// Find an id of an item in an array if the item exists, else return 0
const getIdByValueFromArray = (array, key, value) => {
  if (!Array.isArray(array) || array.length === 0) {
    return 0; // Return 0 or handle as needed
  }
  if (typeof key !== 'string' || key.trim() === '') {
    return 0; // Return 0 or handle as needed
  }

  const item = array.find((obj) => obj[key] === value);
  return item ? item[key] : 0; // Return ID or 0 if not found
};

// Helper function to extract only required fields
const extractFields = (record, requiredFields) => {
  if (requiredFields.length === 0) {
    // If no requiredFields are specified, return the full record
    return { ...record };
  }

  const selectedFields = {};
  requiredFields.forEach((field) => {
    selectedFields[field] = record[field];
  });
  return selectedFields;
};

// Add a new record with only required fields
const AddRecordToArray = (newRecord, existingArray, comparisonField, requiredFields = []) => {
  const extractedRecord = extractFields(newRecord, requiredFields);

  // Check for duplicates
  const isDuplicate = existingArray.some(
    (record) => record[comparisonField] === extractedRecord[comparisonField]
  );

  if (isDuplicate) {
    console.warn("Duplicate record. Not adding to state.");
    return existingArray; // Return the unchanged state
  }

  // Return the updated state with the new record
  return [...existingArray, extractedRecord];
};

// Update an existing record with only required fields
const UpdateRecordInArray = (updatedRecord, existingArray, comparisonField, requiredFields = []) => {
  const extractedRecord = extractFields(updatedRecord, requiredFields);

  // Check if the record to update exists in the state
  const recordIndex = existingArray.findIndex(
    (record) => record[comparisonField] === extractedRecord[comparisonField]
  );

  if (recordIndex === -1) {
    console.warn("Record not found. No updates made.");
    return existingArray; // Return the unchanged state
  }

  // Update the record in the state
  const updatedState = [...existingArray];
  updatedState[recordIndex] = {
    ...updatedState[recordIndex],
    ...extractedRecord, // Merge updated fields with existing record
  };

  return updatedState;
};


/**
 * Compares two objects to check if the type of the objects are same,
 * If the values of the objects are same.
 * @param {Object} object1 - First object to compare.
 * @param {Object} object2 - Second object to compare.
 * @returns {true or false} - Compare result is returned as either true or false.
 */
const CompareTwoObjects = (object1, object2) => {
  // Check if both are objects
  if (typeof object1 !== 'object' || typeof object2 !== 'object') {
    return object1 === object2;
  }

  // Get the keys of both objects
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  // Check if the number of keys is the same
  if (keys1.length !== keys2.length) {
    return false;
  }

  // Recursively compare each key's value
  for (let key of keys1) {
    if (!CompareTwoObjects(object1[key], object2[key])) {
      return false;
    }
  }

  return true;
};


/**
 * Validates and maps an API data object to a predefined object structure.
 * @param {Object} data - The API data to validate and map.
 * @param {Object} defaultObject - The default object structure for validation.
 * @returns {Object} - The validated and mapped object.
 */
const validateAndStoreObject = (data, defaultObject) => {
  // Validate and map the API data to the default object
  return Object.keys(defaultObject).reduce((result, key) => {
    if (key in data) {
      result[key] = data[key];
    }
    return result;
  }, { ...defaultObject });
};

/**
 * Validates and adds an API data object to an array.
 * @param {Object} data - The API data to validate and add.
 * @param {Array} array - The current array of objects.
 * @param {String} comparisonField - The field to use for duplicate checks.
 * @param {*} comparisonValue - The value to check for duplicates.
 * @param {Object} defaultObject - The default object structure for validation.
 * @returns {Array} - The updated array.
 */
const validateAndAddToArray = (data, array, comparisonField, comparisonValue, defaultObject) => {
  // Validate API data
  const validatedObject = Object.keys(defaultObject).reduce((result, key) => {
    if (key in data) {
      result[key] = data[key];
    }
    return result;
  }, { ...defaultObject });

  // Check if the comparison value already exists in the array
  const exists = array.some(item => item[comparisonField] === comparisonValue);

  if (!exists) {
    // Add the validated object to the array
    return [...array, validatedObject];
  } else {
    return array; // Return the unchanged array
  }
};


/**
 * Combines a single object with each object in an array of objects.
 * @param {Object} singleObject - The single object to combine.
 * @param {Array} objectArray - The array of objects to combine with the single object.
 * @returns {Array} - The resulting array of combined objects.
 */
const combineObjectWithArray = (singleObject, objectArray) => {
  return objectArray.map(arrayItem => ({
    ...singleObject,
    ...arrayItem,
  }));
};



// Function to map API response to target object with error handling
const mapApiResponseToObject = (response, mapping) => {
  const targetObject = {};

  // Validate inputs
  if (!response || typeof response !== "object") {
    return targetObject; // Return empty object
  }

  if (!mapping || typeof mapping !== "object" || Object.keys(mapping).length === 0) {
    return targetObject; // Return empty object
  }

  // Loop through the field mapping
  Object.keys(mapping).forEach((targetField) => {
    const sourceField = mapping[targetField];
    let value = null;
    let foundField = false;

    // Handle array of possible source fields
    if (Array.isArray(sourceField)) {
      // Try each source field in the array until we find one that exists
      for (const field of sourceField) {
        // console.log(`  Checking field: "${field}" (type: ${typeof field})`);
        // console.log(`  response.hasOwnProperty("${field}"):`, response.hasOwnProperty(field));
        // console.log(`  "${field}" in response:`, field in response);
        if (response.hasOwnProperty(field)) {
          value = response[field];
          foundField = true;
          break; // Use the first match found
        }
      }
    } else if (typeof sourceField === 'string' && response.hasOwnProperty(sourceField)) {
      // Handle single string source field
      value = response[sourceField];
      foundField = true;
    }

    if (foundField) {
      // Handle undefined or null values with defaults
      if (value === undefined || value === null) {
        targetObject[targetField] = ""; // Default value
      } else {
        // Transform or directly map the value
        if (targetField === "age" && typeof value === "number") {
          targetObject[targetField] = value + 1; // Example transformation
        } else {
          targetObject[targetField] = value;
        }
      }
    } else {
      // No matching source field found
      targetObject[targetField] = ""; // Default for missing fields
    }
  });

  return targetObject;
};

const findArrayDifferencesByField = (array1, array2, comparisonField) => {
  // Create Sets for the dynamic key values of both arrays
  const values1 = new Set(array1.map(item => item[comparisonField]));
  const values2 = new Set(array2.map(item => item[comparisonField]));

  // Find items in array1 but not in array2
  const uniqueToFirstArray = array1.filter(item => !values2.has(item[comparisonField]));

  // Find items in array2 but not in array1
  const uniqueToSecondArray = array2.filter(item => !values1.has(item[comparisonField]));

  // Combine the differences
  const differencesArray = [...uniqueToFirstArray, ...uniqueToSecondArray];

  if (differencesArray.length > 0) { return true; }
  else { return false; }
};

const checkFileExistsInPath = async (filePath) => {
  if (filePath) {
    try {
      const response = await axios.head(filePath);
      if (response.status === 200) {
        return true;
      }
      else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }
  else {
    return false;
  }
};


// Helper function to update form values
function updateArrayStateValues(arrayStateSetter, updatedValues) {
  arrayStateSetter((prevValues) => ({
    ...prevValues,
    ...updatedValues
  }));
}

function getParentIdByChildId(data, parentFieldName, childFieldName, childField, childId) {
  try {
      const parent = data.find((parent) =>
          parent[childFieldName].some((child) => child[childField] === childId)
      );
      return parent ? parent[parentFieldName] : 0; // Return 0 if no parent is found
  }
  catch (error) {
      
  }
}

const filterArrayByCondition = (arrayToFilter, condition) => {
  try {
    const result = arrayToFilter.filter((item) => condition(item));
    return result;
  } catch (error) {
    return [];
  }
}


function itemsArray(n) {
  // Takes a total number of pages and returns an array of that length with the indexes
  if (n <= 0) return []; // Handle edge case for non-positive integers

  const arr = new Array(n);
  arr[0] = 0;
  arr[n - 1] = n - 1;

  for (let i = 1; i < n - 1; i++) {
    arr[i] = i;
  }

  return arr;
}

function differenceArray(arr1, arr2) {
  // Returns the difference between two arrays. All the items in arr1 that are not in arr2
  const result = [];

  for (let i = 0; i < arr1.length; i++) {
    if (!arr2.includes(arr1[i])) {
      result.push(arr1[i]);
    }
  }

  return result;
}

function getCurrentDateTimeString() {
  const utcTime = new Date().toISOString(); // Get current time in ISO format
  const dateObj = new Date(utcTime); // Create a Date object from the UTC time

  // Extract UTC components
  const year = dateObj.getUTCFullYear(); // Full year (e.g., 2023)
  const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0'); // Month (0-11, so add 1)
  const day = String(dateObj.getUTCDate()).padStart(2, '0'); // Day of the month
  const hours = String(dateObj.getUTCHours()).padStart(2, '0'); // Hours (0-23)
  const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0'); // Minutes (0-59)
  const seconds = String(dateObj.getUTCSeconds()).padStart(2, '0'); // Seconds (0-59)

  // Format into "yyyyMMddHHmmss"
  return `${year}${month}${day}${hours}${minutes}${seconds}`;
}


function getCurrentDateTimeString2() {
  const now = new Date().toISOString();
  const dateNowObj = new Date(now); 

  const year = dateNowObj.getFullYear();
  const month = String(dateNowObj.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(dateNowObj.getDate()).padStart(2, '0');
  const hours = String(dateNowObj.getHours()).padStart(2, '0');
  const minutes = String(dateNowObj.getMinutes()).padStart(2, '0');
  const seconds = String(dateNowObj.getSeconds()).padStart(2, '0');

  return `${year}${month}${day}${hours}${minutes}${seconds}`;
}

const getColumnCSSbySize = (refs, setStatesCSS, threshold = [768]) => {
  refs.forEach((ref, index) => {
    if (ref.current) {
      if (ref.current.offsetWidth <= threshold[index]) {
        setStatesCSS[index]('col-yt-1');
      }
      else if (ref.current.offsetWidth <= 1000) {
        setStatesCSS[index]('col-yt-2');
      }
      else if (ref.current.offsetWidth <= 1500) {
        setStatesCSS[index]('col-yt-3');
      }
      else {
        setStatesCSS[index]('col-yt-4');
      }
    }
  });
};

const initializeResizeListener = (refs, setStatesCSS, threshold = 768) => {
  const resizeHandler = () => getColumnCSSbySize(refs, setStatesCSS, threshold);
  window.addEventListener("resize", resizeHandler);
  resizeHandler(); // Initial call
  return () => window.removeEventListener("resize", resizeHandler); // Cleanup
};

const getRandomFourDigitNumber = () => {
  return Math.floor(1000 + Math.random() * 9000);
};

const windowHistoryPushState = (currentUrl) => {
  const currentPath = currentUrl;
  let indexToAppendToUrl = getRandomFourDigitNumber();
  let currentPathUrl = `${currentPath}?idxLock=${indexToAppendToUrl + 1}`;
  window.history.pushState({ trap: true }, '', currentPathUrl);
  indexToAppendToUrl = 0;
  currentPathUrl = `${currentPath}?idxLock=${indexToAppendToUrl + 1}`;
  window.history.pushState({ trap: true }, '', currentPathUrl);
  return currentPathUrl;
};

const GetSelectedColumns = (jsonData, desiredColumns) => {
  const filteredData = jsonData.map(item =>
    desiredColumns.reduce((obj, key) => {
      if (item.hasOwnProperty(key)) {
        obj[key] = item[key];
      }
      return obj;
    }, {})
  );
  return filteredData;
};

// ...existing code...

// Function to parse "LastName, FirstName" format into separate components
const parseFullName = (fullNameString) => {
  if (!fullNameString || typeof fullNameString !== 'string') {
    return {
      firstName: '',
      lastName: ''
    };
  }

  // Trim whitespace and split by comma
  const trimmedName = fullNameString.trim();
  const parts = trimmedName.split(',');

  if (parts.length < 2) {
    // No comma found, assume it's just a single name (treat as first name)
    return {
      firstName: trimmedName,
      lastName: ''
    };
  }

  const lastName = parts[0].trim();
  const firstName = parts[1].trim();

  return {
    firstName,
    lastName
  };
};

// SSN formatting function from G2 - removes spaces AND hyphens
const formatSSN = (ssn) => {
  if (!ssn) return '';
  return ssn.replace(/[ -]/g, '').trim(); // Remove spaces AND hyphens
};

// Add this function to UtilityLibrary.js around line 450
const getCaseInfoFromSession = (caseNumberSearched) => {
  try {
    let cpSSN = "";
    let ncpSSN = "";
    
    if (!caseNumberSearched) {
      return { cpSSN, ncpSSN };
    }

    const caseInfoFromSession = SessionManager.GetDataFromSession(SessionManager.CaseInformationKey);
    
    if (caseInfoFromSession) {
      let caseInfoArray = [];
      
      // Handle both array and single object responses
      if (Array.isArray(caseInfoFromSession)) {
        caseInfoArray = caseInfoFromSession;
      } else {
        caseInfoArray = [caseInfoFromSession];
      }
      
      const currentCaseInfo = caseInfoArray.find(caseItem => 
        caseItem.caseNumber === caseNumberSearched
      );
      
      if (currentCaseInfo) {

        // G2 field mapping: Look for formatted SSN fields first, then raw SSN fields
        const rawCpSSN = currentCaseInfo.cpSSNFormatted || currentCaseInfo.cpssn || currentCaseInfo.CPSSN || '';
        const rawNcpSSN = currentCaseInfo.ncpSSNFormatted || currentCaseInfo.ncpssn || currentCaseInfo.NCPSSN || '';
        
        // Apply G2 formatting: remove spaces and hyphens
        cpSSN = formatSSN(rawCpSSN);
        ncpSSN = formatSSN(rawNcpSSN);
        


      } else {
        console.warn('🔍 Case not found in session:', caseNumberSearched);
      }
    } else {
      console.warn('🔍 No case information found in session');
    }
    

    return { cpSSN, ncpSSN };
  }
  catch (error) {
    console.error('🔍 Error retrieving case SSN information:', error);
    return { cpSSN: "", ncpSSN: "" };
  }
};

// Add this function to UtilityLibrary.js around line 450
const createAndSubmitDocTransferLog = async (params) => {
  try {
    const {
      formValues,
      updatedDocValues,
      postResponseData,
      loginUserInfo,
      sessionLoginUser,
      stateID = null,
      isHandleSaveAnotherClick = false
    } = params;

    // Only process for Mississippi state
    if (sessionLoginUser.stateID !== EnumUtility.StateIDKey.Mississippi.ID) {
      return { success: true, message: 'Not applicable for this state' };
    }

    // Get SSN values from session
    const { cpSSN, ncpSSN } = getCaseInfoFromSession(formValues.caseNumberSearched);


    // Create the document transfer log object with G2's correct field mapping
    const docTransferLog = {
      userLoginID: loginUserInfo.legacyUserID,
      yTracSiteID: sessionLoginUser.siteID,
      batchID: updatedDocValues.batchID,
      batchDocumentID: updatedDocValues.batchDocumentID,
      documentName: updatedDocValues.documentName,
      caseNumber: updatedDocValues.caseNumber,
      contactRoleID: 0,
      stateDocTypeName: updatedDocValues.stateDocTypeName || "",
      stateDocTransferKey: 0,
      stateStatusName: "PENDING",
      stateResponse: "",
      notes: "Document saved - iConnect transfer initiated",
      // G2 MAPPING: regCol = NCP SSN, reqCol = CP SSN (already formatted)
      regCol: ncpSSN || "", // NCP SSN (Non-Custodial Parent)
      reqCol: cpSSN || "",  // CP SSN (Custodial Parent)
      processCompleteFlag: 0
    };

    // Submit the document transfer log
    const result = await ApiManager.PostDocTransferLogG4ToApi(docTransferLog);

    return { 
      success: true, 
      message: 'Document transfer log created successfully',
      data: result 
    };

  } catch (error) {
    console.error('🔍 Error creating document transfer log:', error);
    return { 
      success: false, 
      message: `Error creating document transfer log: ${error.message}`,
      error: error 
    };
  }
};

// =============================================================================
// Mainframe Error Handling Functions
// =============================================================================

/**
 * Handles mainframe search error status codes and returns a standardized error message
 * @param {number|string|Object} statusCodeOrError - Status code, error type, or error object
 * @returns {Promise<Object|null>} Formatted error object with NotificationType and Message, or null if not handled
 */
const handleMainframeSearchError = async (statusCodeOrError) => {
    let statusCode = null;
    let errorType = null;

    // Handle different input types
    if (typeof statusCodeOrError === 'number') {
        statusCode = statusCodeOrError;
    } else if (typeof statusCodeOrError === 'string') {
        errorType = statusCodeOrError;
    } else if (statusCodeOrError && statusCodeOrError.Details && statusCodeOrError.Details.type) {
        errorType = statusCodeOrError.Details.type;
    } else if (statusCodeOrError && statusCodeOrError.statusCode) {
        statusCode = statusCodeOrError.statusCode;
    } else {
        return null; // Not a handled error type
    }

    // Get the message content ID based on the error
    const messageContentID = getMainframeMessageContentID(statusCode, errorType);
    if (!messageContentID) {
        return null; // Not a handled mainframe error
    }

    try {
        const messageContent = await SharedService.getMessageContent(messageContentID);
        const formattedMessage = messageContent?.messageText?.replace(/\\n/g, "\u00A0\n") ?? "";
        
        return {
            NotificationType: 'error',
            Message: formattedMessage
        };
    } catch (error) {
        console.error('Error fetching mainframe message content:', error);
        return {
            NotificationType: 'error',
            Message: 'An error occurred while processing the mainframe request.'
        };
    }
};

/**
 * Maps status codes or error types to message content IDs
 * @param {number} statusCode - Numeric status code
 * @param {string} errorType - String error type name
 * @returns {number|null} Message content ID or null if not found
 */
const getMainframeMessageContentID = (statusCode, errorType) => {
    // Handle numeric status codes
    if (statusCode) {
        if (statusCode === EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID) {
            return ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System;
        }
        if (statusCode === EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.ID) {
            return ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
        }
    }

    // Handle string error types
    if (errorType) {
        if (errorType === EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.Name) {
            return ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System;
        }
        if (errorType === EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.Name) {
            return ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
        }
    }

    return null;
};

/**
 * Checks if a status code or error is a handled mainframe search error
 * @param {number|string|Object} statusCodeOrError - Status code, error type, or error object
 * @returns {boolean} True if this is a handled mainframe error
 */
const isMainframeSearchError = (statusCodeOrError) => {
    return getMainframeMessageContentID(
        typeof statusCodeOrError === 'number' ? statusCodeOrError : null,
        typeof statusCodeOrError === 'string' ? statusCodeOrError : 
            statusCodeOrError?.Details?.type || null
    ) !== null;
};

/**
 * Checks if a status code array includes mainframe search errors
 * @param {number[]} statusCodes - Array of status codes to check
 * @returns {boolean} True if any status code is a mainframe search error
 */
const includesMainframeSearchError = (statusCodes) => {
    const mainframeErrorCodes = [
        EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID,
        EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.ID
    ];
    return statusCodes.some(code => mainframeErrorCodes.includes(code));
};

// Add this function to UtilityLibrary.js
const checkMainframeConnectivityWithErrorHandling = async (apiMainframeUrl, handleNotificationDialog) => {
    try {
        const checkMainframeConnection = await ApiManager.CheckMainframeConnectivity(apiMainframeUrl);

        // Check error in connectivity
        if (!checkMainframeConnection || (checkMainframeConnection.statusCode !== EnumUtility.CaseSearchResultCode.ConnectionTestSuccess.ID)) {
            // Try to handle the specific mainframe error first
            const errorMessage = await UtilityLibrary.handleMainframeSearchError(checkMainframeConnection?.statusCode);
            
            if (errorMessage) {
                // Show the specific mainframe error message
                handleNotificationDialog(errorMessage);
            } else {
                // Fallback to generic connectivity message for other types of connection failures
                handleNotificationDialog({
                    NotificationType: 'error',
                    Message: 'Unable to connect to the mainframe. Please check your connection and try again.'
                });
            }
            
            return false; // Indicates should exit early
        }
        
        return true; // Indicates can continue
    } catch (error) {
        // Handle any unexpected errors
        handleNotificationDialog({
            NotificationType: 'error',
            Message: 'An unexpected error occurred while checking mainframe connectivity.'
        });
        return false;
    }
};

// Helper to safely parse int or return null
  const toIntOrNull = (val) => {
    if (val === null || val === undefined || val === "") return null;
    const parsed = parseInt(val, 10);
    return isNaN(parsed) ? null : parsed;
  };

  // Helper to safely parse int or return 0 (for non-nullable int fields)
  const toInt = (val, fallback = 0) => {
    if (val === null || val === undefined || val === "") return fallback;
    const parsed = parseInt(val, 10);
    return isNaN(parsed) ? fallback : parsed;
  };

  // Helper: bool/truthy → byte (0 or 1)
  const toByte = (val) => {
    if (val === null || val === undefined) return 0;
    if (typeof val === "boolean") return val ? 1 : 0;
    return parseInt(val, 10) === 1 ? 1 : 0;
};

/**
 * Checks if the current user and date match the mock data test configuration
 * @returns {boolean} True if using mock data, false otherwise
 */
const isUsingMockData = () => {
    const { TARGET_TEST_DATE, TARGET_USER_ID } = MOCK_DATA_TARGET;

    const today = new Date();
    const todayString = today.getFullYear().toString() +
        (today.getMonth() + 1).toString().padStart(2, '0') +
        today.getDate().toString().padStart(2, '0');

    const loginUserInfo = SessionManager.GetValueFromSession(SessionManager.LoginUserInfoKey);
    const currentUserID = loginUserInfo ? JSON.parse(loginUserInfo).userInfoID : null;

    return todayString === TARGET_TEST_DATE && currentUserID === TARGET_USER_ID;
};


  // Helper to resolve nested object paths and return the value
  const getValueByPath = (obj, path) => {
    if (!path) return "";
    return path.split(".").reduce((curr, key) => curr?.[key], obj);
  };

  const formatTemplateValue = (value, noteTemplateParmTypeID) => {
    if (value === null || value === undefined) return "";

    if (noteTemplateParmTypeID === MasterEnum.NoteTemplateParmType.Property_Date_Only) {
      // reuse existing utility format in this file
      return formatDate(value, "MM/DD/YYYY");
    }

    if (typeof value === "object") return value.name ?? JSON.stringify(value);
    return String(value);
};

  const NarrativeBuilder = async (paramsForG4Narr, context) => {
      const g4NoteTemplate = await ApiManager.GetNoteTemplate(paramsForG4Narr);
      const template = g4NoteTemplate[0]?.noteTemplateText;
      if (!template) return "";

      const placeholderMap = g4NoteTemplate.reduce((map, item) => {
          if (item?.parmPlaceholder) {
              map[item.parmPlaceholder] = {
                  propertyName_G4: item.propertyName_G4,
                  noteTemplateParmTypeID: item.noteTemplateParmTypeID
              };
          }
          return map;
      }, {});

      return template.replace(/\{\d+\}/g, (token) => {
          const entry = placeholderMap[token];
          if (!entry) return token;
          const path = entry.propertyName_G4;
          if (!path) return token;
          let value = getValueByPath(context, path);
          if (value === null || value === undefined) return "";

          if (entry.noteTemplateParmTypeID === MasterEnum.NoteTemplateParmType.Property_Date_Only) {
              const d = new Date(value);
              value = !isNaN(d.getTime()) ? formatDate(d.toISOString(), "MM/DD/YYYY") : String(value).split("T")[0];
          }

          if (typeof value === "object") return value.name ?? JSON.stringify(value);
          return String(value);
      });

  };

  const Multipart_Narrative_Consolidator = (narrativeTexts = []) => {
    const siteSettingDetail = JSON.parse(SessionManager.GetValueFromSession(SessionManager.SiteSettingsDetailsKey)) || {};
    const delimter = siteSettingDetail.Multipart_Narrative_Delimiter ?? '`';
    const connector = siteSettingDetail.Multipart_Narrative_Connector ?? ', ';

    if (!Array.isArray(narrativeTexts) || narrativeTexts.length === 0) {
        return "";
    }

    const ordered = [...narrativeTexts]
        .filter((n) => typeof n === "string" && n.length > 0)
        .sort((a, b) => a.split(delimter).length - b.split(delimter).length);

    let narrativeText = "";
    let firstPart = "";

    for (let i = 0; i < ordered.length; i++) {
        const split = ordered[i].split(delimter);

        if (split.length === 1) {
            if (narrativeText.length > 0) narrativeText += "  ";
            narrativeText += split[0];
            continue;
        }

        let isFirstMultipart = false;
        if (!firstPart) {
            isFirstMultipart = true;
            firstPart = split[0];
        }

        const current = split[1] ?? "";

        if (isFirstMultipart) {
            if (narrativeText.length > 0) narrativeText += "  ";
            narrativeText += firstPart;
        }

        if (!isFirstMultipart && current) {
            narrativeText += connector;
        }

        narrativeText += current;
    }

    return narrativeText;
};

const DetermineIfMainframeHost = () => {
    const integrationInfo = JSON.parse(
        SessionManager.GetValueFromSession(SessionManager.IntegrationInfoKey) || '[]'
    );
    const hostCategoryType = Number(MasterEnum.IntegrationCategoryType.Host);
    const mainframeHostType = Number(MasterEnum.IntegrationType.Mainframe_Host);
    return Array.isArray(integrationInfo) &&
        integrationInfo.some(integration =>
            Number(integration?.integrationCategoryTypeID) === hostCategoryType &&
            Number(integration?.integrationTypeID) === mainframeHostType
        );
};

const DetermineIfAPIHost = () => {
    const integrationInfo = JSON.parse(
        SessionManager.GetValueFromSession(SessionManager.IntegrationInfoKey) || '[]'
    );
    const hostCategoryType = Number(MasterEnum.IntegrationCategoryType.Host);
    const mainframeHostType = Number(MasterEnum.IntegrationType.Mainframe_Host);
    return Array.isArray(integrationInfo) &&
        integrationInfo.some(integration =>
            Number(integration?.integrationCategoryTypeID) === hostCategoryType &&
            Number(integration?.integrationTypeID) !== mainframeHostType      // If it's a host but not a mainframe host, then it's an API host
        );
};

const pushNarrativeToWebAPI = async (loginStateID, isNoCase, narrativeText, { caseNumber, onFailure, formName }) => {
  if (loginStateID !== EnumUtility.StateIDKey.Kansas.ID || isNoCase || isUsingMockData()) {
    return true;
  }

  try {
    const paramsForPush = {
      caseId: caseNumber,
      note: narrativeText,
    };

    await ApiManager.AddCaseNarrativeKSFromApi(paramsForPush);
    return true;
  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, formName, "pushNarrativeToWebAPI");
    onFailure?.(error?.ApiProblemDetails?.detail);
    return false;
  }
};

const pushNarrativeToMainframeIfNeeded = async (loginStateID, isNoCase, narrativeText, { apiMainframeUrl, caseNumber, onFailure, formName }) => {

  if (isNoCase || isUsingMockData()) {
    return true;
  }

  try {
    const paramsForPush = {
      stateID: loginStateID,
      apiMainframeUrl,
      caseNumber,
      narrativeText,
    };
    await ScrapeDataManager.PushNarrativeToMainframe(paramsForPush);
    return true;
  } catch (error) {
    await LoggerUtility.ManageErrorLogging(error, formName, "pushNarrativeToMainframeIfNeeded");
    onFailure?.();
    return false;
  }

};

const writeExternalNarrative = async (loginStateID, isNoCase, narrativeText, { caseNumber, onFailure, formName }) => {

  const config = await _getAppConfig();
  const apiMainframeUrl = config.apiMainframeUrl;

  if (DetermineIfMainframeHost()) {
    return await pushNarrativeToMainframeIfNeeded(loginStateID, isNoCase, narrativeText, { apiMainframeUrl, caseNumber, onFailure, formName });
  }

  const siteSettingDetail = JSON.parse(SessionManager.GetValueFromSession(SessionManager.SiteSettingsDetailsKey) || '{}');

  if (DetermineIfAPIHost() && siteSettingDetail.ApiWriteNarrative === "1") {
    return await pushNarrativeToWebAPI(loginStateID, isNoCase, narrativeText, { caseNumber, onFailure, formName });
  }

  return true;
};


const UtilityLibrary = {
  AddRecordToArray,
  UpdateRecordInArray,
  getIdByValueFromArray,
  formatDate,
  cleanString,
  formatString,
  StringBuilder,
  parseFullName,
  formatSSN, // Export G2's SSN formatting function
  dateSortComparator,
  displayDateFormatted,
  getCurrentDate,
  calculateDateDifference,

  CompareTwoObjects,
  validateAndStoreObject,
  validateAndAddToArray,
  combineObjectWithArray,
  mapApiResponseToObject,
  findArrayDifferencesByField,
  filterArrayByCondition,
  checkFileExistsInPath,
  updateArrayStateValues,
  getParentIdByChildId,

  itemsArray,
  differenceArray,
  getCurrentDateTimeString,
  getCurrentDateTimeForTimeZone: SiteTimeUtility.getCurrentDateTimeForTimeZone,
  getDateKeyForTimeZone: SiteTimeUtility.getDateKeyForTimeZone,
  formatDateTimeForTimeZone: SiteTimeUtility.formatDateTimeForTimeZone,

  initializeResizeListener,
  getRandomFourDigitNumber,
  windowHistoryPushState,

  GetSelectedColumns,
  getCaseInfoFromSession,
  createAndSubmitDocTransferLog,

  // Add the new mainframe error handling functions
  handleMainframeSearchError,
  getMainframeMessageContentID,
  isMainframeSearchError,
  includesMainframeSearchError,
  checkMainframeConnectivityWithErrorHandling,
  toIntOrNull,
  toInt,
  toByte,
  isUsingMockData,
  getValueByPath,         
  NarrativeBuilder,
  Multipart_Narrative_Consolidator,
  DetermineIfMainframeHost,
  DetermineIfAPIHost,
  writeExternalNarrative
};

// Export all functions as a single object
export default UtilityLibrary;
